import React from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/AppIcon";
import { cn } from "../../utils/cn";
import ModuleBrochure, {
  type BrochureFeature,
  type BrochureMetric,
  type BrochureUseCase,
} from "./ModuleBrochure";

/**
 * Recruitment brochure — Phase 6 sales surface for the hiring pipeline.
 *
 * Tone: "the hiring loop has a single source of truth". Pitch is calibrated to
 * the hiring manager who is sick of asking "how did the previous round go?"
 * and the HR lead who wants every panel to use the same rubric.
 *
 * Illustration: a synthetic kanban board with 3 columns (Sourced / Interview /
 * Offer) holding 1-2 candidate avatar pills each, plus a floating accent pill
 * labeled "Offer signed" that pulls the eye to the happy end-state.
 *
 * Mockup: a faux pipeline kanban — 3 columns × 2 candidate cards (name + role
 * + score chip + days-in-stage), with a small "This week" stat card on the
 * trailing edge so the screenshot doesn't read as a flat board.
 */

// ----- Illustration: 3-column kanban + "Offer signed" floating pill -----

interface KanbanCandidate {
  initials: string;
  tint: string;
  nameKey: string;
}

const RecruitmentIllustration: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.dir() === "rtl";

  const COLUMNS: { headingKey: string; chipBg: string; chipText: string; cards: KanbanCandidate[] }[] = [
    {
      headingKey: "brochure.recruitment.illustration.col.sourced",
      chipBg: "bg-slate-100",
      chipText: "text-slate-600",
      cards: [
        {
          initials: "MA",
          tint: "bg-sky-100 text-sky-700",
          nameKey: "brochure.recruitment.illustration.candidate.maya",
        },
        {
          initials: "NB",
          tint: "bg-emerald-100 text-emerald-700",
          nameKey: "brochure.recruitment.illustration.candidate.nadir",
        },
      ],
    },
    {
      headingKey: "brochure.recruitment.illustration.col.interview",
      chipBg: "bg-sky-50",
      chipText: "text-sky-700",
      cards: [
        {
          initials: "LF",
          tint: "bg-amber-100 text-amber-800",
          nameKey: "brochure.recruitment.illustration.candidate.layla",
        },
      ],
    },
    {
      headingKey: "brochure.recruitment.illustration.col.offer",
      chipBg: "bg-emerald-50",
      chipText: "text-emerald-700",
      cards: [
        {
          initials: "YO",
          tint: "bg-violet-100 text-violet-700",
          nameKey: "brochure.recruitment.illustration.candidate.yousef",
        },
      ],
    },
  ];

  return (
    <div className="relative aspect-[4/5] w-full">
      {/* Atmospheric SVG — soft grid + sky-tinted glow under the board. */}
      <svg
        viewBox="0 0 320 400"
        className="absolute inset-0 h-full w-full"
        aria-hidden="true"
      >
        <defs>
          <pattern id="rec-grid" width="28" height="28" patternUnits="userSpaceOnUse">
            <path
              d="M 28 0 L 0 0 0 28"
              fill="none"
              stroke="rgb(14 165 233 / 0.08)"
              strokeWidth="1"
            />
          </pattern>
          <radialGradient id="rec-glow" cx="50%" cy="55%" r="55%">
            <stop offset="50%" stopColor="rgb(14 165 233 / 0)" />
            <stop offset="100%" stopColor="rgb(14 165 233 / 0.18)" />
          </radialGradient>
        </defs>
        <rect width="320" height="400" fill="url(#rec-grid)" />
        <ellipse cx="160" cy="220" rx="150" ry="140" fill="url(#rec-glow)" />
      </svg>

      {/* Board */}
      <div className="absolute inset-0 m-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.25)]">
        <div className="flex items-center justify-between px-1">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-sky-700">
            {t("brochure.recruitment.illustration.board")}
          </p>
          <span className="inline-flex h-5 items-center gap-1 rounded-md bg-slate-50 px-1.5 text-[9px] font-medium text-slate-500 ring-1 ring-slate-200">
            <Icon name="Filter" size={9} aria-hidden="true" />
            {t("brochure.recruitment.illustration.allRoles")}
          </span>
        </div>

        <div className="mt-2 grid h-[calc(100%-1.75rem)] grid-cols-3 gap-1.5">
          {COLUMNS.map((col, ci) => (
            <div
              key={ci}
              className="flex flex-col gap-1.5 rounded-lg bg-slate-50/70 p-1.5"
            >
              <div className="flex items-center justify-between px-0.5">
                <p
                  className={cn(
                    "rounded px-1 py-0.5 text-[8px] font-semibold uppercase tracking-wider",
                    col.chipBg,
                    col.chipText,
                  )}
                >
                  {t(col.headingKey)}
                </p>
                <span className="text-[8px] font-semibold tabular-nums text-slate-400">
                  {col.cards.length}
                </span>
              </div>
              {col.cards.map((c, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-1.5 py-1.5 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                >
                  <span
                    className={cn(
                      "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[8px] font-semibold",
                      c.tint,
                    )}
                  >
                    {c.initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[9px] font-semibold text-slate-900">
                      {t(c.nameKey)}
                    </p>
                    <p className="text-[8px] text-slate-400">
                      {t("brochure.recruitment.illustration.roleSample")}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Floating accent pill — "Offer signed". */}
      <div
        className={cn(
          "absolute bottom-[6%] flex items-center gap-1.5 rounded-full bg-sky-600 px-3 py-1.5 text-[10px] font-semibold text-white shadow-[0_14px_30px_-8px_rgba(2,132,199,0.55)] ring-2 ring-white",
          isRtl ? "left-[6%]" : "right-[6%]",
        )}
      >
        <Icon name="CheckCircle2" size={12} aria-hidden="true" />
        <span>{t("brochure.recruitment.illustration.offerSigned")}</span>
      </div>
    </div>
  );
};

// ----- Mockup: faux kanban pipeline + "This week" stat card -----

interface PipelineCard {
  initials: string;
  tint: string;
  nameKey: string;
  roleKey: string;
  score: number;
  daysKey: string;
}

interface PipelineColumn {
  headingKey: string;
  chipBg: string;
  chipText: string;
  cards: PipelineCard[];
}

const PIPELINE: PipelineColumn[] = [
  {
    headingKey: "brochure.recruitment.mockup.col.sourced",
    chipBg: "bg-slate-100",
    chipText: "text-slate-700",
    cards: [
      {
        initials: "MA",
        tint: "bg-sky-100 text-sky-700",
        nameKey: "brochure.recruitment.mockup.candidate.maya",
        roleKey: "brochure.recruitment.mockup.role.backend",
        score: 78,
        daysKey: "brochure.recruitment.mockup.days.2",
      },
      {
        initials: "NB",
        tint: "bg-emerald-100 text-emerald-700",
        nameKey: "brochure.recruitment.mockup.candidate.nadir",
        roleKey: "brochure.recruitment.mockup.role.product",
        score: 71,
        daysKey: "brochure.recruitment.mockup.days.4",
      },
    ],
  },
  {
    headingKey: "brochure.recruitment.mockup.col.interview",
    chipBg: "bg-sky-50",
    chipText: "text-sky-700",
    cards: [
      {
        initials: "LF",
        tint: "bg-amber-100 text-amber-800",
        nameKey: "brochure.recruitment.mockup.candidate.layla",
        roleKey: "brochure.recruitment.mockup.role.backend",
        score: 84,
        daysKey: "brochure.recruitment.mockup.days.6",
      },
      {
        initials: "KA",
        tint: "bg-rose-100 text-rose-700",
        nameKey: "brochure.recruitment.mockup.candidate.karim",
        roleKey: "brochure.recruitment.mockup.role.design",
        score: 80,
        daysKey: "brochure.recruitment.mockup.days.3",
      },
    ],
  },
  {
    headingKey: "brochure.recruitment.mockup.col.offer",
    chipBg: "bg-emerald-50",
    chipText: "text-emerald-700",
    cards: [
      {
        initials: "YO",
        tint: "bg-violet-100 text-violet-700",
        nameKey: "brochure.recruitment.mockup.candidate.yousef",
        roleKey: "brochure.recruitment.mockup.role.product",
        score: 92,
        daysKey: "brochure.recruitment.mockup.days.1",
      },
      {
        initials: "DS",
        tint: "bg-indigo-100 text-indigo-700",
        nameKey: "brochure.recruitment.mockup.candidate.dana",
        roleKey: "brochure.recruitment.mockup.role.design",
        score: 88,
        daysKey: "brochure.recruitment.mockup.days.2",
      },
    ],
  },
];

const scoreTone = (score: number): string => {
  if (score >= 85) return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (score >= 75) return "bg-sky-50 text-sky-700 ring-sky-200";
  return "bg-slate-50 text-slate-600 ring-slate-200";
};

const RecruitmentMockup: React.FC = () => {
  const { t } = useTranslation();
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_220px]">
      {/* Pipeline board */}
      <div className="overflow-hidden rounded-lg border border-slate-200">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-4 py-2.5">
          <div className="flex items-center gap-2">
            <Icon name="Kanban" size={14} className="text-slate-500" aria-hidden="true" />
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              {t("brochure.recruitment.mockup.heading")}
            </p>
          </div>
          <p className="text-[10px] font-medium text-slate-400">
            {t("brochure.recruitment.mockup.roleFilter")}
          </p>
        </div>

        <div className="grid grid-cols-3 gap-2 bg-white p-3">
          {PIPELINE.map((col, ci) => (
            <div key={ci} className="flex flex-col gap-2">
              <div className="flex items-center justify-between px-0.5">
                <p
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider",
                    col.chipBg,
                    col.chipText,
                  )}
                >
                  {t(col.headingKey)}
                </p>
                <span className="text-[9px] font-semibold tabular-nums text-slate-400">
                  {col.cards.length}
                </span>
              </div>
              {col.cards.map((c, idx) => (
                <div
                  key={idx}
                  className="rounded-md border border-slate-200 bg-white p-2 shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                >
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold",
                        c.tint,
                      )}
                    >
                      {c.initials}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[10px] font-semibold text-slate-900">
                        {t(c.nameKey)}
                      </p>
                      <p className="truncate text-[9px] text-slate-500">{t(c.roleKey)}</p>
                    </div>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between">
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[9px] font-semibold tabular-nums ring-1",
                        scoreTone(c.score),
                      )}
                    >
                      <Icon name="Star" size={8} aria-hidden="true" />
                      {c.score}
                    </span>
                    <span className="text-[9px] text-slate-400 tabular-nums">
                      {t(c.daysKey)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* "This week" stat card */}
      <div className="grid gap-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
            {t("brochure.recruitment.mockup.thisWeek")}
          </p>
          <div className="mt-2 space-y-2 text-xs">
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-600">
                <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                {t("brochure.recruitment.mockup.statSourced")}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">5</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-600">
                <span className="h-1.5 w-1.5 rounded-full bg-sky-500" />
                {t("brochure.recruitment.mockup.statInterviewed")}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">3</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-slate-600">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t("brochure.recruitment.mockup.statOffer")}
              </span>
              <span className="font-semibold tabular-nums text-slate-900">1</span>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-sky-700">
            {t("brochure.recruitment.mockup.timeToHire")}
          </p>
          <p className="mt-1.5 text-2xl font-semibold tabular-nums text-sky-900">
            {t("brochure.recruitment.mockup.timeToHireValue")}
          </p>
          <p className="mt-0.5 text-[10px] leading-snug text-sky-800/80">
            {t("brochure.recruitment.mockup.timeToHireHint")}
          </p>
        </div>
      </div>
    </div>
  );
};

// ----- Brochure props ------

const RecruitmentBrochure: React.FC = () => {
  const { t } = useTranslation();

  const features: BrochureFeature[] = [
    {
      icon: "Briefcase",
      title: t("brochure.recruitment.features.jobOpenings.title"),
      description: t("brochure.recruitment.features.jobOpenings.description"),
    },
    {
      icon: "Kanban",
      title: t("brochure.recruitment.features.pipeline.title"),
      description: t("brochure.recruitment.features.pipeline.description"),
    },
    {
      icon: "ClipboardList",
      title: t("brochure.recruitment.features.kits.title"),
      description: t("brochure.recruitment.features.kits.description"),
    },
    {
      icon: "FilePen",
      title: t("brochure.recruitment.features.offers.title"),
      description: t("brochure.recruitment.features.offers.description"),
    },
    {
      icon: "Star",
      title: t("brochure.recruitment.features.scorecards.title"),
      description: t("brochure.recruitment.features.scorecards.description"),
    },
    {
      icon: "BarChart3",
      title: t("brochure.recruitment.features.analytics.title"),
      description: t("brochure.recruitment.features.analytics.description"),
    },
  ];

  const metrics: BrochureMetric[] = [
    {
      value: "32%",
      label: t("brochure.recruitment.metrics.fasterLabel"),
    },
    {
      value: t("brochure.recruitment.metrics.meetingValue"),
      label: t("brochure.recruitment.metrics.meetingLabel"),
    },
    {
      value: "100%",
      label: t("brochure.recruitment.metrics.feedbackLabel"),
    },
  ];

  const useCases: BrochureUseCase[] = [
    {
      persona: t("brochure.recruitment.useCases.manager.persona"),
      scenario: t("brochure.recruitment.useCases.manager.scenario"),
      outcome: t("brochure.recruitment.useCases.manager.outcome"),
    },
    {
      persona: t("brochure.recruitment.useCases.hr.persona"),
      scenario: t("brochure.recruitment.useCases.hr.scenario"),
      outcome: t("brochure.recruitment.useCases.hr.outcome"),
    },
  ];

  return (
    <ModuleBrochure
      slug="recruitment"
      hero={{
        eyebrow: t("brochure.recruitment.hero.eyebrow"),
        headline: t("brochure.recruitment.hero.headline"),
        subheadline: t("brochure.recruitment.hero.subheadline"),
        illustration: <RecruitmentIllustration />,
      }}
      features={features}
      metrics={metrics}
      mockup={{
        title: t("brochure.recruitment.mockup.caption"),
        content: <RecruitmentMockup />,
      }}
      useCases={useCases}
    />
  );
};

export default RecruitmentBrochure;
